import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronRight, Heart, Clock, Flame, Trash2, ExternalLink, Utensils, Link2,
  Pencil, Check, X, Plus, Image as ImageIcon,
} from "lucide-react";

const CAT_LABEL = { sweet: "حلو", savory: "مالح", beverage: "مشروبات", sauces: "صلصات" };
const PLATFORM_LABEL = { tiktok: "TikTok", instagram: "Instagram", youtube: "YouTube", facebook: "Facebook", twitter: "X", other: "رابط" };

export default function ImportedRecipeDetailPage() {
  const { recipeId } = useParams();
  const [r, setR] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = () => api.get(`/user-recipes/${recipeId}`).then((res) => setR(res.data));
  useEffect(() => { load().catch((e) => toast.error(extractError(e))); /* eslint-disable-next-line */ }, [recipeId]);

  const startEdit = () => { setDraft({ ...r }); setEditMode(true); };
  const cancelEdit = () => { setDraft(null); setEditMode(false); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: draft.title, short_description: draft.short_description,
        category: draft.category, ingredients: draft.ingredients, steps: draft.steps,
        prep_minutes: draft.prep_minutes, cook_minutes: draft.cook_minutes,
        servings: draft.servings, calories_per_serving: draft.calories_per_serving,
        protein_g: draft.protein_g, carbs_g: draft.carbs_g, fat_g: draft.fat_g,
      };
      await api.patch(`/user-recipes/${recipeId}`, payload);
      toast.success("تم حفظ التعديلات");
      setEditMode(false); setDraft(null);
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const toggleFav = async () => {
    const { data } = await api.post(`/user-recipes/${recipeId}/favorite`);
    setR((x) => ({ ...x, is_favorite: data.is_favorite }));
    toast.success(data.is_favorite ? "أُضيفت للمفضلة" : "أُزيلت من المفضلة");
  };

  const del = async () => {
    if (!window.confirm("حذف الوصفة نهائياً؟")) return;
    await api.delete(`/user-recipes/${recipeId}`);
    toast.success("تم الحذف");
    nav("/recipes?tab=mine");
  };

  if (!r) return <div className="text-center py-12 text-muted-foreground">جاري التحميل...</div>;

  const view = editMode ? draft : r;

  return (
    <div className="space-y-5 max-w-3xl mx-auto lg:mx-0" data-testid={`imported-recipe-${r.id}`}>
      <div>
        <Link to="/recipes?tab=mine" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
          <ChevronRight className="h-4 w-4" /> وصفاتي المستوردة
        </Link>
      </div>

      {/* Video / thumbnail card */}
      <Card className="overflow-hidden">
        <div className="relative aspect-video bg-black">
          {r.stream_iframe_url ? (
            <iframe
              src={r.stream_iframe_url}
              title={r.title}
              className="w-full h-full border-0"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              data-testid="stream-player"
            />
          ) : (r.gallery_images || []).length > 0 ? (
            <img src={r.gallery_images[0]} alt={r.title} className="w-full h-full object-cover" />
          ) : r.source_thumbnail ? (
            <img src={r.source_thumbnail} alt={r.title} className="w-full h-full object-cover" />
          ) : r.image_url ? (
            <img src={r.image_url} alt={r.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/40"><Utensils className="h-14 w-14" /></div>
          )}
          <span className="absolute top-3 start-3 bg-white/95 text-xs px-2 py-1 rounded-full font-medium inline-flex items-center gap-1 z-10">
            <Link2 className="h-3 w-3" /> {PLATFORM_LABEL[r.source_platform] || "رابط"}
          </span>
        </div>

        {/* Photo gallery — for imported photo/carousel posts */}
        {(r.gallery_images || []).length > 1 && (
          <div className="p-3 border-t border-foreground/10" data-testid="gallery-strip">
            <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <ImageIcon className="h-3 w-3" /> {r.gallery_images.length} صور من المنشور
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {r.gallery_images.map((img, i) => (
                <a
                  key={i}
                  href={img}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0"
                  data-testid={`gallery-img-${i}`}
                >
                  <img
                    src={img}
                    alt={`صورة ${i + 1}`}
                    className="h-20 w-20 sm:h-24 sm:w-24 object-cover rounded-lg border border-foreground/10 hover:border-primary transition"
                  />
                </a>
              ))}
            </div>
          </div>
        )}
        <div className="p-3 flex items-center justify-between flex-wrap gap-2 text-sm">
          <a href={r.source_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
            <ExternalLink className="h-3.5 w-3.5" /> فتح الفيديو الأصلي
          </a>
          <div className="flex gap-1">
            {!editMode ? (
              <>
                <Button variant="ghost" size="sm" onClick={startEdit} className="gap-1" data-testid="btn-edit-imp">
                  <Pencil className="h-4 w-4" /> تعديل
                </Button>
                <Button variant="ghost" size="sm" onClick={toggleFav} className="gap-1" data-testid="btn-fav-imp">
                  <Heart className={`h-4 w-4 ${r.is_favorite ? "fill-current text-accent" : ""}`} /> {r.is_favorite ? "مفضّلة" : "مفضلة"}
                </Button>
                <Button variant="ghost" size="sm" onClick={del} className="text-destructive gap-1" data-testid="btn-del-imp">
                  <Trash2 className="h-4 w-4" /> حذف
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={saveEdit} disabled={saving} className="gap-1" data-testid="btn-save-edit">
                  <Check className="h-4 w-4" /> {saving ? "جاري الحفظ..." : "حفظ"}
                </Button>
                <Button variant="outline" size="sm" onClick={cancelEdit} className="gap-1" data-testid="btn-cancel-edit">
                  <X className="h-4 w-4" /> إلغاء
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Recipe details */}
      <Card className="p-5 sm:p-6 space-y-5">
        {editMode ? (
          <EditForm draft={draft} setDraft={setDraft} />
        ) : (
          <ReadView r={view} />
        )}
      </Card>
    </div>
  );
}

function ReadView({ r }) {
  return (
    <>
      <div>
        <div className="text-xs text-muted-foreground">
          {CAT_LABEL[r.category] || r.category} · {(r.prep_minutes || 0) + (r.cook_minutes || 0)} دقيقة · {r.servings || 1} حصص
        </div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold mt-1">{r.title}</h1>
        {r.short_description && <p className="text-sm text-muted-foreground mt-2">{r.short_description}</p>}
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <NutriPill icon={Flame} label="سعرات" val={`${r.calories_per_serving || 0}`} />
        <NutriPill label="بروتين" val={`${r.protein_g || 0}غ`} />
        <NutriPill label="كربوهيدرات" val={`${r.carbs_g || 0}غ`} />
        <NutriPill label="دهون" val={`${r.fat_g || 0}غ`} />
      </div>
      <div>
        <h3 className="font-display font-semibold mb-2">المكونات</h3>
        <ul className="space-y-1.5 text-sm">
          {(r.ingredients || []).map((it, i) => (
            <li key={i} className="flex items-baseline gap-2 py-1 border-b border-border/50">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-2" />
              <span className="font-medium">{it.amount} {it.unit}</span>
              <span className="text-foreground/80">{it.name}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="font-display font-semibold mb-2">الخطوات</h3>
        <ol className="space-y-2 text-sm">
          {(r.steps || []).map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary font-bold text-xs grid place-items-center shrink-0">{i + 1}</span>
              <span className="flex-1 pt-0.5 leading-relaxed">{s}</span>
            </li>
          ))}
        </ol>
      </div>
    </>
  );
}

function EditForm({ draft, setDraft }) {
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const addIng = () => set({ ingredients: [...(draft.ingredients || []), { name: "", amount: "", unit: "" }] });
  const updIng = (i, patch) => set({ ingredients: draft.ingredients.map((x, k) => k === i ? { ...x, ...patch } : x) });
  const rmIng = (i) => set({ ingredients: draft.ingredients.filter((_, k) => k !== i) });

  const addStep = () => set({ steps: [...(draft.steps || []), ""] });
  const updStep = (i, val) => set({ steps: draft.steps.map((x, k) => k === i ? val : x) });
  const rmStep = (i) => set({ steps: draft.steps.filter((_, k) => k !== i) });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>العنوان</Label>
        <Input value={draft.title || ""} onChange={(e) => set({ title: e.target.value })} data-testid="edit-title" />
      </div>
      <div className="space-y-2">
        <Label>الوصف القصير</Label>
        <Textarea value={draft.short_description || ""} onChange={(e) => set({ short_description: e.target.value })} rows={2} data-testid="edit-desc" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">الفئة</Label>
          <Select value={draft.category || "savory"} onValueChange={(v) => set({ category: v })}>
            <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sweet">حلو</SelectItem>
              <SelectItem value="savory">مالح</SelectItem>
              <SelectItem value="beverage">مشروبات</SelectItem>
              <SelectItem value="sauces">صلصات</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">تحضير (د)</Label><Input type="number" min={0} value={draft.prep_minutes} onChange={(e) => set({ prep_minutes: +e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">طبخ (د)</Label><Input type="number" min={0} value={draft.cook_minutes} onChange={(e) => set({ cook_minutes: +e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">حصص</Label><Input type="number" min={1} value={draft.servings} onChange={(e) => set({ servings: +e.target.value })} /></div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <div className="space-y-1"><Label className="text-xs">سعرات/حصة</Label><Input type="number" min={0} value={draft.calories_per_serving} onChange={(e) => set({ calories_per_serving: +e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">بروتين (غ)</Label><Input type="number" min={0} step="0.1" value={draft.protein_g} onChange={(e) => set({ protein_g: +e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">كربوهيدرات (غ)</Label><Input type="number" min={0} step="0.1" value={draft.carbs_g} onChange={(e) => set({ carbs_g: +e.target.value })} /></div>
        <div className="space-y-1"><Label className="text-xs">دهون (غ)</Label><Input type="number" min={0} step="0.1" value={draft.fat_g} onChange={(e) => set({ fat_g: +e.target.value })} /></div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>المكونات</Label>
          <Button type="button" size="sm" variant="outline" onClick={addIng} className="gap-1" data-testid="edit-add-ing">
            <Plus className="h-3 w-3" /> مكوّن
          </Button>
        </div>
        <div className="space-y-1">
          {(draft.ingredients || []).map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_90px_auto] gap-1.5" data-testid={`edit-ing-${i}`}>
              <Input placeholder="الاسم" value={it.name} onChange={(e) => updIng(i, { name: e.target.value })} />
              <Input placeholder="الكمية" value={it.amount} onChange={(e) => updIng(i, { amount: e.target.value })} />
              <Input placeholder="الوحدة" value={it.unit} onChange={(e) => updIng(i, { unit: e.target.value })} />
              <Button size="icon" variant="ghost" onClick={() => rmIng(i)} className="text-destructive"><X className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>الخطوات</Label>
          <Button type="button" size="sm" variant="outline" onClick={addStep} className="gap-1" data-testid="edit-add-step">
            <Plus className="h-3 w-3" /> خطوة
          </Button>
        </div>
        <div className="space-y-1.5">
          {(draft.steps || []).map((s, i) => (
            <div key={i} className="flex items-start gap-2" data-testid={`edit-step-${i}`}>
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary font-bold text-xs grid place-items-center shrink-0 mt-2">{i + 1}</span>
              <Textarea value={s} onChange={(e) => updStep(i, e.target.value)} rows={2} className="flex-1" />
              <Button size="icon" variant="ghost" onClick={() => rmStep(i)} className="text-destructive mt-1"><X className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NutriPill({ icon: Icon, label, val }) {
  return (
    <div className="rounded-xl bg-secondary p-3">
      <div className="text-lg font-bold flex items-center justify-center gap-1">
        {Icon && <Icon className="h-4 w-4 text-primary" />}
        {val}
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
